import { isAccountAdmin, scopeCoursesToUser } from '../../utils/scope';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BookOpen, CircleAlert, CircleCheck, FilePenLine, LayoutGrid, Plus, Users, X } from 'lucide-react';
import CourseListCard from './CourseListCard';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import Pagination from '../../components/Pagination/Pagination';
import SearchInput from '../../components/SearchInput/SearchInput';
import { deleteCourse, setCoursePublished } from '../../api/courses';
import { fetchAllCourses } from '../../hooks/useAllCourses';
import { useAuth } from '../../context/AuthContext';
import { canCreateCourse, canDeleteCourse, validatePermission } from '../../utils/permissions';
import { formatNumber } from '../../utils/datetime';
import Dropdown from '../../components/Dropdown/Dropdown';
import { useI18n } from '../../i18n/I18nContext';
import { pickPlural } from './plural';
import './Courses.css';

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const PAGE_SIZE = 20;

// How each sort option orders the cards (the list is sorted here, over the whole catalogue).
const SORT_KEYS = {
  createdAt: (c) => new Date(c.createdAt || 0).getTime(),
  title: (c) => (c.title || '').toLowerCase(),
  price: (c) => c.price || 0,
};

/** Turn a stored thumbnail into a loadable URL: pass full/data URLs through,
 *  prefix root-relative paths with the API origin, else leave as-is. Returns
 *  null when empty so the card can show its branded fallback (no stock image). */
function resolveThumb(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (/^(https?:)?\/\//i.test(u) || u.startsWith('data:')) return u;
  if (u.startsWith('/')) return `${API_ORIGIN}${u}`;
  return u;
}

function mapCourseToCard(c, currentUserId) {
  return {
    id: c.id,
    title: c.title,
    lessonsTotal: c._count?.lessons ?? 0,
    enrollmentsTotal: c._count?.enrollments ?? 0,
    testsTotal: c._count?.quizzes ?? c._count?.exams ?? 0,
    // Real backend cover (resolved). null → branded fallback in the card.
    image: resolveThumb(c.thumbnail),
    description: c.description || null,
    level: c.level || null,
    category: c.category || null,
    createdAt: c.createdAt || null,
    price: Number(c.price) || 0,
    currency: c.currency || 'UZS',
    isPublished: c.isPublished,
    createdById: c.createdById,
    // The API names the author (`createdBy: { id, name }`), it never sends an e-mail address.
    ownerName: c.createdBy?.name || c.createdBy?.email || null,
    isMine: Boolean(currentUserId && c.createdById === currentUserId),
  };
}

const Courses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  // The list could not load: 'forbidden' (403) or 'failed'. Shown as a calm translated state,
  // never as the server's own text.
  const [loadError, setLoadError] = useState(null);
  // A refused or failed delete. Unlike `loadError` it is a banner above the list, which stays on screen.
  const [actionError, setActionError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [reloadTick, setReloadTick] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const deletingRef = useRef(false);
  // Courses whose publish state is being changed right now (a second click on one is ignored).
  const publishingRef = useRef(new Set());
  const sectionRef = useRef(null);
  const shownPageRef = useRef(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // A new load starts clean: the last failure (or refused delete) must not hide the list for good.
    setLoadError(null);
    setActionError(null);
    // The whole catalogue is read, not one page of it: the counters, the status and price filters
    // and the staff scoping (a teacher sees the courses they created) all work on every course,
    // and the page, search and sort below are applied here.
    fetchAllCourses({ sortBy: 'createdAt', sortOrder: 'desc' })
      .then((all) => {
        if (cancelled) return;
        const mine = scopeCoursesToUser(all, user);
        setCourses(mine.map((c) => mapCourseToCard(c, user?.id)));
      })
      .catch((err) => {
        if (cancelled) return;
        // The API client rejects { message, code } where code is the HTTP status.
        setLoadError(err?.code === 403 ? 'forbidden' : 'failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, reloadTick]);

  const filteredCourses = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    const list = courses.filter((c) => {
      if (term && !`${c.title || ''} ${c.description || ''}`.toLowerCase().includes(term)) return false;
      if (statusFilter === 'published' && !c.isPublished) return false;
      if (statusFilter === 'draft' && c.isPublished) return false;
      if (priceFilter === 'paid' && !(c.price > 0)) return false;
      if (priceFilter === 'free' && c.price > 0) return false;
      return true;
    });
    const key = SORT_KEYS[sortBy] || SORT_KEYS.createdAt;
    const dir = sortOrder === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      const x = key(a);
      const y = key(b);
      return (typeof x === 'string' ? x.localeCompare(y) : x - y) * dir;
    });
  }, [courses, searchQuery, statusFilter, priceFilter, sortBy, sortOrder]);

  // Counters cover every course the user works with, not the page on screen.
  const stats = useMemo(() => {
    const total = courses.length;
    const live = courses.filter((c) => c.isPublished).length;
    const draft = total - live;
    // Enrollments, not people: a student in three courses counts three times, so the tile says so.
    const enrollments = courses.reduce((sum, c) => sum + (c.enrollmentsTotal || 0), 0);
    return { total, live, draft, enrollments };
  }, [courses]);

  // What this person may do with courses; the API refuses the rest, so the page does not offer it.
  const mayCreate = canCreateCourse(user);
  const mayDelete = canDeleteCourse(user);
  const mayPublish = isAccountAdmin(user);

  const handleDelete = useCallback((course) => {
    if (!mayDelete) {
      setActionError(t('courses.deletePermissionError'));
      return;
    }
    setActionError(null);
    setCourseToDelete(course);
  }, [mayDelete, t]);

  const confirmDelete = useCallback(async () => {
    if (!courseToDelete || deletingRef.current) return;
    try {
      validatePermission(user, 'deleteCourse');
    } catch {
      setActionError(t('courses.deletePermissionError'));
      setCourseToDelete(null);
      return;
    }
    deletingRef.current = true;
    setIsDeleting(true);
    const { id } = courseToDelete;
    try {
      await deleteCourse(id);
      setCourses((prev) => prev.filter((c) => c.id !== id));
      setActionError(null);
      toast.success(t('courses.deletedToast'));
    } catch (err) {
      setActionError(err?.code === 403 ? t('courses.deletePermissionError') : t('courses.deleteFailed'));
    } finally {
      deletingRef.current = false;
      setIsDeleting(false);
      // Close the dialog either way: its scrim would cover the banner that explains a failure.
      setCourseToDelete(null);
    }
  }, [courseToDelete, user, t]);

  const cancelDelete = useCallback(() => {
    if (!deletingRef.current) setCourseToDelete(null);
  }, []);

  // Publish or unpublish from the card's menu. The chip, the tiles and the status filter follow
  // the new state at once; a refusal or failure is the banner above the list, in our own words.
  const handleTogglePublish = useCallback(async (course) => {
    if (!mayPublish) {
      setActionError(t('courses.publishPermissionError'));
      return;
    }
    if (publishingRef.current.has(course.id)) return;
    publishingRef.current.add(course.id);
    setActionError(null);
    const publish = !course.isPublished;
    try {
      const updated = await setCoursePublished(course.id, publish);
      const isPublished = typeof updated?.isPublished === 'boolean' ? updated.isPublished : publish;
      setCourses((prev) => prev.map((c) => (c.id === course.id ? { ...c, isPublished } : c)));
      toast.success(t(isPublished ? 'courses.publishedToast' : 'courses.unpublishedToast'));
    } catch (err) {
      setActionError(err?.code === 403 ? t('courses.publishPermissionError') : t('courses.publishFailed'));
    } finally {
      publishingRef.current.delete(course.id);
    }
  }, [mayPublish, t]);

  const handleCreate = () => navigate('/courses/create');
  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / PAGE_SIZE));
  // A shorter list (a filter, a deleted card) can leave the page number past the end.
  const currentPage = Math.min(page, totalPages);
  const pageCourses = filteredCourses.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hasResults = filteredCourses.length > 0;
  const hasCourses = courses.length > 0;
  const hasFilters = statusFilter !== 'all' || priceFilter !== 'all' || Boolean(searchQuery);
  // The tiles, search and filters are for working a catalogue. They show while it loads (as
  // skeletons, so the page does not jump) and once there are courses; a failed load or an empty
  // catalogue is just its state, not a row of zeros above it.
  const showControls = loading || (!loadError && hasCourses);

  // Bring the list back to its top when the page changes: the pager sits BELOW the cards, so a
  // click on "next" leaves the reader at the foot of the new page. Compared with the page we
  // already showed (not a "has mounted" flag): React's dev double-run of effects must not scroll
  // a freshly opened page away from its header. Typing in the toolbar never scrolls: the list's top
  // is still below the scroller's edge then.
  useEffect(() => {
    if (shownPageRef.current === currentPage) return;
    shownPageRef.current = currentPage;
    const list = sectionRef.current;
    if (!list || typeof list.scrollIntoView !== 'function') return;
    const scroller = list.closest('.content-area');
    const edge = scroller ? scroller.getBoundingClientRect().top : 0;
    if (list.getBoundingClientRect().top < edge) list.scrollIntoView({ block: 'start' });
  }, [currentPage]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriceFilter('all');
    setPage(1);
  };

  // A KPI tile that is also a status filter. "All" is the way back to everything and is never
  // drawn as selected (a ring on load would read as a stuck focus state); the other two toggle,
  // and wear the ring only once they are the filter in force.
  const filterTile = (status, label, value, icon, tone) => {
    const active = status !== 'all' && statusFilter === status;
    return (
      <MetricCard
        title={label}
        value={value}
        icon={icon}
        tone={tone}
        loading={loading}
        active={active}
        onClick={() => { setStatusFilter(status === 'all' || active ? 'all' : status); setPage(1); }}
      />
    );
  };

  return (
    <div className="courses-page">
      <header className="page-head">
        <div className="page-head-main">
          <span className="page-head-icon" aria-hidden="true"><BookOpen /></span>
          <div>
            <h1 className="page-title">{t('courses.title')}</h1>
            <p className="page-sub">{t('courses.subtitle')}</p>
          </div>
        </div>
        {mayCreate && (
          <div className="page-actions">
            <button type="button" className="ds-btn ds-btn-primary" onClick={handleCreate}>
              <Plus aria-hidden="true" />
              {t('courses.createButton')}
            </button>
          </div>
        )}
      </header>

      <div className="ui-stack">
        {showControls && (
          <>
            {/* KPI tiles — the first three are also the status filter */}
            <div className="kpi-row">
              {filterTile('all', t('courses.statTotal'), stats.total, <LayoutGrid />, 'brand')}
              {filterTile('published', t('courses.statPublished'), stats.live, <CircleCheck />, 'success')}
              {filterTile('draft', t('courses.statDrafts'), stats.draft, <FilePenLine />, 'coral')}
              <MetricCard
                title={t('courses.statEnrollments')}
                value={stats.enrollments}
                icon={<Users />}
                tone="info"
                loading={loading}
              />
            </div>

            {/* Search + filters */}
            <div className="courses-toolbar">
              <SearchInput
                className="courses-search"
                value={searchQuery}
                onChange={(value) => { setSearchQuery(value); setPage(1); }}
                placeholder={t('courses.searchPlaceholder')}
                aria-label={t('courses.searchPlaceholder')}
              />
              <Dropdown
                value={priceFilter}
                onChange={(val) => { setPriceFilter(val); setPage(1); }}
                options={[
                  { value: 'all', label: t('courses.filterAllPrices') },
                  { value: 'paid', label: t('courses.filterPaidOption') },
                  { value: 'free', label: t('courses.filterFreeOption') },
                ]}
                className="courses-select"
                size="sm"
                ariaLabel={t('courses.filterByPriceAria')}
              />
              <Dropdown
                value={`${sortBy}:${sortOrder}`}
                onChange={(val) => {
                  const [sb, so] = val.split(':');
                  setSortBy(sb);
                  setSortOrder(so);
                  setPage(1);
                }}
                options={[
                  { value: 'createdAt:desc', label: t('courses.sortNewest') },
                  { value: 'createdAt:asc', label: t('courses.sortOldest') },
                  { value: 'title:asc', label: t('courses.sortTitleAsc') },
                  { value: 'title:desc', label: t('courses.sortTitleDesc') },
                  { value: 'price:desc', label: t('courses.sortPriceHighLow') },
                  { value: 'price:asc', label: t('courses.sortPriceLowHigh') },
                ]}
                className="courses-select"
                size="sm"
                ariaLabel={t('courses.sortAria')}
              />
              {/* How many are shown ("8 ta kurs"; "8 tadan 3 tasi" + clear while a filter narrows the
                  list), read out politely as it changes. With no match at all the empty state below
                  carries the one "clear filters" button. */}
              {hasResults && !loading && (
                <div className={`courses-filter-state${hasFilters ? '' : ' is-idle'}`} aria-live="polite">
                  <span className="courses-count">
                    {hasFilters
                      ? t('courses.resultsMeta', {
                        shown: formatNumber(filteredCourses.length, { decimals: 0 }),
                        total: formatNumber(courses.length, { decimals: 0 }),
                      })
                      : pickPlural(
                        t('courses.countMeta', { n: formatNumber(filteredCourses.length, { decimals: 0 }) }),
                        filteredCourses.length,
                        lang,
                      )}
                  </span>
                  {hasFilters && (
                    <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={clearFilters}>
                      {t('courses.clearFilters')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {actionError && (
          <div className="ui-alert" data-tone="danger" role="alert">
            <CircleAlert aria-hidden="true" />
            <p>{actionError}</p>
            <button
              type="button"
              className="ds-btn ds-btn-ghost ds-btn-icon ds-btn-sm courses-alert-close"
              aria-label={t('courses.dismissAria')}
              onClick={() => setActionError(null)}
            >
              <X aria-hidden="true" />
            </button>
          </div>
        )}

        {loadError && (
          // A refused read (403) is the lock with no retry (asking again changes nothing);
          // any other failure is the error state with the ink "Qaytadan" button.
          <PageEmptyState
            variant={loadError === 'forbidden' ? 'forbidden' : 'error'}
            title={t('courses.errorTitle')}
            description={loadError === 'forbidden' ? t('courses.noAccess') : t('courses.errorDescription')}
            onRetry={() => setReloadTick((n) => n + 1)}
          />
        )}

        {!loadError && (
          <div className="courses-section" ref={sectionRef}>
            {loading ? (
              <LoadingState skeleton="cards" count={6} message={t('courses.loadingMessage')} />
            ) : !hasResults ? (
              hasCourses && hasFilters ? (
                <PageEmptyState
                  variant="search"
                  title={t('courses.noMatchTitle')}
                  description={t('courses.noMatchDescription')}
                  actionLabel={t('courses.clearFilters')}
                  onAction={clearFilters}
                />
              ) : (
                <PageEmptyState
                  variant="docs"
                  title={t('courses.emptyTitle')}
                  description={t('courses.emptyDescription')}
                  actionLabel={mayCreate ? t('courses.emptyCreateButton') : undefined}
                  onAction={mayCreate ? handleCreate : undefined}
                />
              )
            ) : (
              <div className="card-grid">
                {pageCourses.map((course) => (
                  <CourseListCard
                    key={course.id}
                    course={course}
                    canDelete={mayDelete}
                    canPublish={mayPublish}
                    onDelete={handleDelete}
                    onTogglePublish={handleTogglePublish}
                  />
                ))}
              </div>
            )}

            {!loading && filteredCourses.length > PAGE_SIZE && (
              <div className="courses-pagination">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  total={filteredCourses.length}
                  limit={PAGE_SIZE}
                  onPrev={() => setPage(Math.max(1, currentPage - 1))}
                  onNext={() => setPage(Math.min(totalPages, currentPage + 1))}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(courseToDelete)}
        tone="danger"
        title={t('courses.deleteDialogTitle')}
        description={courseToDelete ? (
          <>
            <strong className="courses-confirm-name">{courseToDelete.title}</strong>
            {' '}
            {t('courses.deleteDialogBody')}
          </>
        ) : null}
        confirmLabel={t('courses.delete')}
        cancelLabel={t('courses.cancel')}
        loading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default Courses;
